// The extension host (spec/dashboard.md section 10.2): one per branch workspace; loads the branch's enabled scripts into
// a sandbox backend (a Worker in the browser, Node's vm in tests), answers the sandbox's API calls through the editor API,
// collects the log, runs the menu actions and the hooks, debounces the field changes into `@onChange`.
import type { Document } from '../api/types';
import type { LogEntry } from '../ui/LogPanel';
import { entityKey, type Draft, type Workspace } from '../workspace/Workspace';
import { createEditorApi, type EditorAdapter } from './editorApi';
import { ONCHANGE_DEBOUNCE_MS, PRESAVE_TIMEOUT_MS, type ChangeEvent, type ChangeHandlerSpec, type HostMessage, type LoadError, type LoadedExtension, type MenuItemSpec, type PostSaveCtx, type PreSaveCtx, type PreSaveOutcome, type SandboxMessage, type ScriptSource } from './protocol';
import { unnyIdOf } from './model';

export interface SandboxBackend {
  start: (onMessage: (m: SandboxMessage) => void) => { post: (m: HostMessage) => void; terminate: () => void };
}

export type HostState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; extensions: LoadedExtension[]; errors: LoadError[] } | { kind: 'failed'; message: string };

export interface MenuNode {
  label: string;
  children: MenuNode[];
  item?: MenuItemSpec;
}

/** The merged menu tree of every extension's `@menuItem` paths: siblings by priority desc, then registration order (10.3). */
export function buildMenu(extensions: LoadedExtension[]): MenuNode[] {
  const root: MenuNode = { label: '', children: [] };
  const items = extensions.flatMap((e) => e.menu);
  for (const item of items) {
    const parts = item.path.split('/').filter(Boolean);
    let node = root;
    for (let i = 0; i < parts.length; i++) {
      const label = parts[i]!;
      let child = node.children.find((c) => c.label === label && (i < parts.length - 1 ? !c.item : true));
      if (!child) {
        child = { label, children: [] };
        node.children.push(child);
      }
      node = child;
    }
    node.item = item;
  }
  const sort = (nodes: MenuNode[]) => {
    nodes.sort((a, b) => (b.item?.priority ?? maxPriority(b)) - (a.item?.priority ?? maxPriority(a)));
    for (const n of nodes) sort(n.children);
  };
  const maxPriority = (n: MenuNode): number => Math.max(n.item?.priority ?? 0, ...n.children.map(maxPriority));
  sort(root.children);
  return root.children;
}

export interface HostOptions {
  backend: SandboxBackend;
  adapter: EditorAdapter;
  loadScripts: () => Promise<ScriptSource[]>;
  onState?: (state: HostState) => void;
  onLog?: (entry: LogEntry) => void;
  /** heirs of a template name, for the `@onChange` template filter */
  heirsOf: (template: string) => string[];
  now?: () => number;
}

export class ExtensionHost {
  private sandbox: { post: (m: HostMessage) => void; terminate: () => void } | null = null;
  private api = new Map<string, (method: string, args: unknown[]) => Promise<unknown>>();
  private callSeq = 0;
  private pendingDone = new Map<string, { resolve: (outcomes: PreSaveOutcome[] | undefined, error?: string) => void }>();
  private readyResolve: ((state: HostState) => void) | null = null;
  private disposedResolve: (() => void) | null = null;
  private logSeq = 0;
  private debounces = new Map<string, ReturnType<typeof setTimeout>>();
  private lastValues = new Map<string, unknown>();
  private unsubscribe: (() => void) | null = null;
  state: HostState = { kind: 'idle' };
  logs: LogEntry[] = [];
  extensions: LoadedExtension[] = [];
  /** the context the last preSave saw, for postSave's changedEntities (10.6) */
  lastPreSaveCtx: PreSaveCtx | null = null;
  /** writes by extensions never fire @onChange (the re-entrancy guard, 10.3) */
  private suppressChanges = 0;

  constructor(private opts: HostOptions) {}

  get workspace(): Workspace {
    return this.opts.adapter.workspace;
  }

  /** The adapter follows the catalog (its templates and strings reload with the branch head). */
  setAdapter(adapter: EditorAdapter): void {
    this.opts = { ...this.opts, adapter };
    for (const name of Array.from(this.api.keys())) this.api.set(name, createEditorApi(adapter, name));
  }

  private setState(state: HostState) {
    this.state = state;
    this.opts.onState?.(state);
  }

  private log(level: LogEntry['level'], source: string, message: string, data?: unknown) {
    const entry: LogEntry = { id: ++this.logSeq, at: (this.opts.now ?? Date.now)(), level, source, message, data };
    this.logs = [entry, ...this.logs].slice(0, 2000);
    this.opts.onLog?.(entry);
  }

  clearLogs(source?: string): void {
    this.logs = source ? this.logs.filter((l) => l.source !== source) : [];
    this.opts.onState?.(this.state); // the shell re-renders on the host's events
  }

  /** Loads (or reloads) the branch's scripts. */
  async load(): Promise<HostState> {
    await this.dispose();
    this.setState({ kind: 'loading' });
    let scripts: ScriptSource[];
    try {
      scripts = await this.opts.loadScripts();
    } catch (e) {
      const state: HostState = { kind: 'failed', message: e instanceof Error ? e.message : String(e) };
      this.setState(state);
      return state;
    }
    if (scripts.length === 0) {
      const state: HostState = { kind: 'ready', extensions: [], errors: [] };
      this.extensions = [];
      this.setState(state);
      return state;
    }
    for (const s of scripts) this.api.set(s.name, createEditorApi(this.opts.adapter, s.name));
    const ready = new Promise<HostState>((resolve) => {
      this.readyResolve = resolve;
    });
    try {
      this.sandbox = this.opts.backend.start((m) => this.onMessage(m));
    } catch (e) {
      const state: HostState = { kind: 'failed', message: e instanceof Error ? e.message : String(e) };
      this.setState(state);
      return state;
    }
    this.sandbox.post({ type: 'load', scripts });
    const state = await ready;
    this.watchChanges();
    return state;
  }

  async dispose(): Promise<void> {
    this.unsubscribe?.();
    this.unsubscribe = null;
    for (const t of this.debounces.values()) clearTimeout(t);
    this.debounces.clear();
    if (!this.sandbox) return;
    const sandbox = this.sandbox;
    const disposed = new Promise<void>((resolve) => {
      this.disposedResolve = resolve;
      setTimeout(resolve, 2000); // a hung onDispose never blocks the reload
    });
    sandbox.post({ type: 'dispose' });
    await disposed;
    sandbox.terminate();
    this.sandbox = null;
    this.extensions = [];
    this.api.clear();
  }

  private onMessage(m: SandboxMessage) {
    switch (m.type) {
      case 'ready': {
        this.extensions = m.extensions;
        const state: HostState = { kind: 'ready', extensions: m.extensions, errors: m.errors };
        this.setState(state);
        this.readyResolve?.(state);
        this.readyResolve = null;
        break;
      }
      case 'disposed':
        this.disposedResolve?.();
        this.disposedResolve = null;
        break;
      case 'log':
        this.log(m.level, m.extension, m.message, m.data);
        break;
      case 'call': {
        // the sandbox names the extension whose code is running (its hook, handler, action or onInit)
        const extension = m.extension && m.extension !== 'extension' ? m.extension : this.extensionOf(m.method, m.id);
        const api = this.api.get(extension) ?? createEditorApi(this.opts.adapter, extension);
        this.suppressChanges++;
        api(m.method, m.args)
          .then((result) => this.sandbox?.post({ type: 'result', id: m.id, result: cloneable(result) }))
          .catch((e: unknown) => this.sandbox?.post({ type: 'result', id: m.id, error: e instanceof Error ? e.message : String(e) }))
          .finally(() => {
            this.suppressChanges--;
            this.snapshotValues();
          });
        break;
      }
      case 'done': {
        const p = this.pendingDone.get(m.callId);
        if (p) {
          this.pendingDone.delete(m.callId);
          p.resolve(m.outcomes, m.error);
        }
        break;
      }
    }
  }

  // the sandbox does not say which extension calls; the current call is attributed to the extension running (the sandbox logs carry it);
  // the API's actor group is therefore the extension the host last saw running a hook or an action, else the first loaded one
  private running: string | null = null;
  private extensionOf(_method: string, _callId: string): string {
    return this.running ?? this.extensions[0]?.name ?? 'extension';
  }

  private done(callId: string): Promise<{ outcomes?: PreSaveOutcome[]; error?: string }> {
    return new Promise((resolve) => this.pendingDone.set(callId, { resolve: (outcomes, error) => resolve({ outcomes, error }) }));
  }

  menu(): MenuNode[] {
    return buildMenu(this.extensions);
  }

  async runMenu(id: string): Promise<void> {
    if (!this.sandbox) return;
    const callId = `m${++this.callSeq}`;
    this.running = id.split('.')[0] ?? null;
    const p = this.done(callId);
    this.sandbox.post({ type: 'menu', callId, id });
    await p;
    this.running = null;
  }

  /** `preSave` on every extension, in name order (10.6). */
  async preSave(ctx: PreSaveCtx, timeoutMs = PRESAVE_TIMEOUT_MS): Promise<PreSaveOutcome[]> {
    if (!this.sandbox || !this.extensions.some((e) => e.hooks.preSave)) return [];
    const callId = `p${++this.callSeq}`;
    const p = this.done(callId);
    this.sandbox.post({ type: 'preSave', callId, ctx, timeoutMs });
    const { outcomes } = await p;
    return outcomes ?? [];
  }

  async postSave(ctx: PostSaveCtx): Promise<void> {
    if (!this.sandbox || !this.extensions.some((e) => e.hooks.postSave)) return;
    const callId = `q${++this.callSeq}`;
    const p = this.done(callId);
    this.sandbox.post({ type: 'postSave', callId, ctx });
    await p;
  }

  // ---- @onChange: the user's field edits, debounced per (entity, parameter), never an extension's own write ----------------

  private snapshotValues() {
    this.lastValues.clear();
    for (const d of this.workspace.all) {
      if (d.kind !== 'document') continue;
      for (const c of d.changes) this.lastValues.set(`${d.key}|${c.path}`, c.after);
    }
  }

  private watchChanges() {
    const handlers = this.extensions.flatMap((e) => e.onChange);
    if (handlers.length === 0) return;
    this.snapshotValues();
    this.unsubscribe = this.workspace.subscribe(() => {
      if (this.suppressChanges > 0) {
        this.snapshotValues();
        return;
      }
      for (const d of this.workspace.all) {
        if (d.kind !== 'document' || d.actor !== 'user') continue;
        for (const c of d.changes) {
          if (!c.path.startsWith('data.')) continue;
          const key = `${d.key}|${c.path}`;
          if (this.lastValues.has(key) && this.lastValues.get(key) === c.after) continue;
          this.lastValues.set(key, c.after);
          this.schedule(d, c.path.slice(5), c.before, c.after, handlers);
        }
      }
    });
  }

  private schedule(draft: Draft, param: string, before: unknown, after: unknown, handlers: ChangeHandlerSpec[]) {
    const doc = draft.current as unknown as Document;
    const matching = handlers.filter((h) => (!h.param || h.param === param) && (!h.template || h.template === doc.template || this.opts.heirsOf(h.template).includes(doc.template)));
    if (matching.length === 0) return;
    const key = `${draft.key}|${param}`;
    const existing = this.debounces.get(key);
    if (existing) clearTimeout(existing);
    this.debounces.set(
      key,
      setTimeout(() => {
        this.debounces.delete(key);
        if (!this.sandbox) return;
        const event: ChangeEvent = { unnyId: unnyIdOf(doc), id: doc.id, templateName: doc.template, paramName: param, newValue: cloneable(after), oldValue: cloneable(before) };
        this.sandbox.post({ type: 'change', event, handlers: matching.map((h) => `${h.extension}.${h.method}`) });
      }, ONCHANGE_DEBOUNCE_MS),
    );
  }
}

function cloneable<T>(value: T): T {
  try {
    return typeof structuredClone === 'function' ? structuredClone(value) : (JSON.parse(JSON.stringify(value)) as T);
  } catch {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}

/** The pre-save context of the user's document drafts (10.6). */
export function changedEntitiesOf(workspace: Workspace, catalogLabel?: (d: Document) => string): PreSaveCtx {
  void catalogLabel;
  const changedEntities = workspace.all
    .filter((d) => d.kind === 'document' && d.actor === 'user')
    .map((d) => {
      const doc = d.current as unknown as Document;
      const params = Array.from(new Set(d.changes.filter((c) => c.path.startsWith('data.')).map((c) => c.path.slice(5).split(/[.[]/)[0]!)));
      return {
        unnyId: unnyIdOf(doc),
        id: doc.id,
        templateName: doc.template,
        changedParams: params,
        changes: d.changes.filter((c) => c.path.startsWith('data.')).map((c) => ({ param: c.path.slice(5), before: cloneable(c.before), after: cloneable(c.after) })),
        isNew: d.status === 'new',
        isDeleted: d.status === 'deleted',
      };
    });
  return { changedEntities };
}

export { entityKey };
